import { forwardRef, memo, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { format, getDaysInMonth, isValid, toDate } from 'date-fns';
import {
  ICLEPeriod,
  IComplianceGroup,
  IEvaluatedJurisdictionView,
  ReportingPeriodType
} from '@/@types/client-api';
import { Root } from '@radix-ui/react-form';
import DropdownSelect from '@/components/ui/DropdownSelect';
import DropdownButton from '@/components/ui/Buttons/DropdownButton';
import { formatOptions } from '@/components/ui/CreditTracker/util/TrackerHelpers';
import Link from '@/components/ui/Link';
import DateIntervalPicker from '@/components/ui/CreditTracker/Forms/DateIntervalPicker';
import DateInput from '@/components/ui/Form/DateInput';
import {
  ComplianceFieldErrors,
  ComplianceFieldNames,
  ComplianceModalMessage
} from '@/components/ui/CreditTracker/util/constants';
import Shimmer from '@/components/ui/Shimmer';

export type ComplianceFormFields = {
  complianceGroup?: IComplianceGroup | null;
  birthMonth?: number;
  birthDay?: number;
  admitDate?: string;
  startDate?: string | null;
  endDate?: string | null;
  specialStatus?: string | null;
} & Pick<
  ICLEPeriod,
  'defaultStartDate' | 'defaultEndDate' | 'hasDefaultDate' | 'isUserSet' | 'ruleSetFlag'
>;

export type ComplianceFormProps = {
  jurisdiction?: IEvaluatedJurisdictionView;
  currentCompliancePeriod?: ComplianceFormFields;
  complianceGroups?: IComplianceGroup[];
  onChange?: (
    key: ComplianceFieldNames,
    value: string | number | IComplianceGroup | undefined
  ) => void;
  fieldsWithErrors?: ComplianceFieldErrors;
  setFieldsWithErrors?: (errors: ComplianceFieldErrors) => void;
  onResetDates?: () => void;
  reportingPeriod?: ReportingPeriodType;
  isUpdatingDateInterval?: boolean;
  isUpdatingForm?: boolean;
  showReset?: boolean;
  selectedPeriod?: string | null;
};

const ComplianceForm = memo(
  forwardRef<HTMLDivElement, ComplianceFormProps>(
    (
      {
        jurisdiction,
        currentCompliancePeriod,
        complianceGroups,
        onChange,
        onResetDates,
        fieldsWithErrors,
        setFieldsWithErrors,
        reportingPeriod,
        isUpdatingDateInterval,
        isUpdatingForm,
        showReset,
        selectedPeriod = 'current'
      }: ComplianceFormProps,
      ref
    ) => {
      const [days, setDays] = useState<number[]>([]);

      const birthDate = useMemo(() => {
        const date = toDate(jurisdiction?.userComplianceInformation?.birthDate ?? '');
        if (isValid(date)) {
          return date;
        }
      }, [jurisdiction?.userComplianceInformation?.birthDate]);

      const months = useMemo(() => {
        return Array.from({ length: 12 }, (_e, i) =>
          new Date(0, i).toLocaleString('default', { month: 'long' })
        );
      }, []);

      const years = useMemo(() => {
        const years = [];
        const currYear = new Date().getFullYear();
        for (let y = currYear; y >= 1900; y--) {
          years.push(y);
        }
        return years;
      }, []);

      const admitYear = useMemo(() => {
        const date = new Date(currentCompliancePeriod?.admitDate ?? '');

        if (isValid(date)) return date.getFullYear();
      }, [currentCompliancePeriod?.admitDate]);

      useLayoutEffect(() => {
        const numOfDays = (() => {
          return !!currentCompliancePeriod?.birthMonth && currentCompliancePeriod?.birthMonth > 0
            ? getDaysInMonth(new Date(2000, currentCompliancePeriod?.birthMonth - 1))
            : getDaysInMonth(new Date());
        })();

        const dayArray = Array.from({ length: numOfDays }, (_, i) => i + 1);
        currentCompliancePeriod?.birthDay &&
          !dayArray.includes(currentCompliancePeriod?.birthDay) &&
          onChange &&
          onChange(ComplianceFieldNames.BIRTH_DAY, undefined);
        setDays(dayArray);
      }, [
        currentCompliancePeriod?.birthDay,
        currentCompliancePeriod?.birthMonth,
        onChange,
        birthDate
      ]);

      const getMonthName = useCallback((monthIndex: number) => {
        return new Date(0, monthIndex).toLocaleString('en', { month: 'long' });
      }, []);

      const rangeExplanationText =
        jurisdiction && jurisdiction.isCalculatedDate
          ? ComplianceModalMessage.CALCULATED_DATE
          : ComplianceModalMessage.DISCLAIMER;

      const shimmer = useMemo(() => {
        return (
          <div className="mb-8 flex flex-col gap-y-8 pt-1">
            <Shimmer className="relative h-[70px] w-full *:h-full" />
            <Shimmer className="relative h-[70px] w-full *:h-full" />
          </div>
        );
      }, []);

      return (
        <Root
          onSubmit={event => event.preventDefault()}
          className="mb-6"
          data-component="ComplianceForm"
        >
          {isUpdatingForm ? (
            shimmer
          ) : (
            <div className="mb-8 flex flex-col gap-y-8 pt-1">
              {complianceGroups &&
                (reportingPeriod === ReportingPeriodType._1 ||
                  reportingPeriod === ReportingPeriodType._5) && (
                  <div className="mb-4 flex flex-row gap-x-4">
                    <div className="relative max-h-[70px] w-full *:h-full [&>button]:h-[70px]">
                      <DropdownSelect
                        button={({ isOpen, value, dropdownSide, ...props }) => (
                          <DropdownButton
                            state={isOpen ? 'open' : 'closed'}
                            label={value}
                            dropdownError={
                              fieldsWithErrors?.[ComplianceFieldNames.COMPLIANCE_GROUP]
                            }
                            dropdownSide={dropdownSide}
                            {...props}
                          />
                        )}
                        label="Compliance group"
                        placeholder="Select a compliance group"
                        options={
                          formatOptions(complianceGroups.map(x => x.longDescription ?? '')) as {
                            label: string;
                            value: string;
                          }[]
                        }
                        value={currentCompliancePeriod?.complianceGroup?.longDescription}
                        onValueChange={value => {
                          onChange &&
                            onChange(
                              ComplianceFieldNames.COMPLIANCE_GROUP,
                              complianceGroups.find(obj => obj.longDescription === value)
                            );
                          setFieldsWithErrors &&
                            setFieldsWithErrors({
                              ...fieldsWithErrors,
                              [ComplianceFieldNames.COMPLIANCE_GROUP]: undefined
                            });
                        }}
                        menuStyle="form-field"
                        required
                      />
                    </div>
                  </div>
                )}
              {((reportingPeriod === ReportingPeriodType._2 &&
                !jurisdiction?.isTransitionalOrNewlyAdmitted) ||
                reportingPeriod === ReportingPeriodType._3) && (
                <div className="flex flex-col gap-x-4 gap-y-8 sm:flex-row sm:gap-y-0">
                  <div className="relative basis-full sm:basis-7/12 [&>button]:h-[70px]" ref={ref}>
                    <DropdownSelect
                      button={({ isOpen, value, dropdownSide, ...props }) => (
                        <DropdownButton
                          state={isOpen ? 'open' : 'closed'}
                          label={value}
                          dropdownError={fieldsWithErrors?.[ComplianceFieldNames.BIRTH_MONTH]}
                          dropdownSide={dropdownSide}
                          {...props}
                        />
                      )}
                      label="Birth month"
                      placeholder="Select a birth month"
                      options={
                        formatOptions(months) as {
                          label: string;
                          value: string;
                        }[]
                      }
                      value={
                        currentCompliancePeriod?.birthMonth
                          ? getMonthName(currentCompliancePeriod?.birthMonth - 1)
                          : ''
                      }
                      onValueChange={value => {
                        onChange &&
                          onChange(ComplianceFieldNames.BIRTH_MONTH, months.indexOf(value) + 1);
                        setFieldsWithErrors &&
                          setFieldsWithErrors({
                            ...fieldsWithErrors,
                            [ComplianceFieldNames.BIRTH_MONTH]: undefined
                          });
                      }}
                      menuStyle="form-field"
                      required
                    />
                  </div>
                  <div className="relative max-h-[70px] w-full basis-full sm:basis-5/12 [&>button]:h-[70px]">
                    <DropdownSelect
                      button={({ isOpen, value, dropdownSide, ...props }) => (
                        <DropdownButton
                          state={isOpen ? 'open' : 'closed'}
                          label={value}
                          dropdownError={fieldsWithErrors?.[ComplianceFieldNames.BIRTH_DAY]}
                          dropdownSide={dropdownSide}
                          {...props}
                        />
                      )}
                      label="Birth day"
                      placeholder="Select a birth day"
                      options={
                        formatOptions(days.map(String)) as {
                          label: string;
                          value: string;
                        }[]
                      }
                      value={currentCompliancePeriod?.birthDay?.toString()}
                      onValueChange={value => {
                        onChange && onChange(ComplianceFieldNames.BIRTH_DAY, parseInt(value));
                        setFieldsWithErrors &&
                          setFieldsWithErrors({
                            ...fieldsWithErrors,
                            [ComplianceFieldNames.BIRTH_DAY]: undefined
                          });
                      }}
                      menuStyle="form-field"
                      required
                    />
                  </div>
                </div>
              )}
              {((reportingPeriod === ReportingPeriodType._2 &&
                jurisdiction?.isTransitionalOrNewlyAdmitted) ||
                reportingPeriod === ReportingPeriodType._4 ||
                reportingPeriod === ReportingPeriodType._5) && (
                <DateInput
                  label="Admission date"
                  onChange={value => {
                    setFieldsWithErrors &&
                      setFieldsWithErrors({
                        ...fieldsWithErrors,
                        [ComplianceFieldNames.ADMIT_DATE]: undefined
                      });
                    onChange && onChange(ComplianceFieldNames.ADMIT_DATE, value);
                  }}
                  value={currentCompliancePeriod?.admitDate}
                  id={'admitDate'}
                  name={'admitDate'}
                  className="mb-3 sm:mb-0"
                  required
                  inheritedErrors={
                    fieldsWithErrors && [
                      {
                        message: fieldsWithErrors?.[ComplianceFieldNames.ADMIT_DATE]?.message,
                        match: fieldsWithErrors?.[ComplianceFieldNames.ADMIT_DATE]?.match,
                        forceMatch: fieldsWithErrors?.[ComplianceFieldNames.ADMIT_DATE]?.forceMatch
                      }
                    ]
                  }
                  forceInvalid={fieldsWithErrors?.[ComplianceFieldNames.ADMIT_DATE]?.showError}
                />
              )}
              {reportingPeriod === ReportingPeriodType._2 &&
                !jurisdiction?.isTransitionalOrNewlyAdmitted && (
                  <div className="mb-4 flex flex-row gap-x-4">
                    <div className="relative max-h-[70px] w-full [&>button]:h-[70px]">
                      <DropdownSelect
                        button={({ isOpen, value, dropdownSide, ...props }) => (
                          <DropdownButton
                            state={isOpen ? 'open' : 'closed'}
                            label={value}
                            dropdownError={fieldsWithErrors?.[ComplianceFieldNames.ADMIT_YEAR]}
                            dropdownSide={dropdownSide}
                            {...props}
                          />
                        )}
                        label="Admission Year"
                        placeholder="Select an admission year"
                        options={
                          formatOptions(years.map(String)) as {
                            label: string;
                            value: string;
                          }[]
                        }
                        value={admitYear?.toString()}
                        onValueChange={value => {
                          onChange &&
                            onChange(
                              ComplianceFieldNames.ADMIT_DATE,
                              format(new Date(parseInt(value), 0, 1), 'MM/dd/yyyy')
                            );
                          setFieldsWithErrors &&
                            setFieldsWithErrors({
                              ...fieldsWithErrors,
                              [ComplianceFieldNames.ADMIT_YEAR]: undefined
                            });
                        }}
                        menuStyle="form-field"
                        required
                      />
                    </div>
                  </div>
                )}
            </div>
          )}
          <div className="flex flex-col gap-y-8 pt-1">
            <DateIntervalPicker
              handleStartDate={value =>
                onChange && onChange(ComplianceFieldNames.START_DATE, value)
              }
              handleEndDate={value => onChange && onChange(ComplianceFieldNames.END_DATE, value)}
              startDate={currentCompliancePeriod?.startDate ?? ''}
              endDate={currentCompliancePeriod?.endDate ?? ''}
              title={`Your ${selectedPeriod ?? 'current'} compliance period is`}
              fieldsWithErrors={fieldsWithErrors}
              setFieldsWithErrors={error => setFieldsWithErrors && setFieldsWithErrors(error)}
              isUpdating={isUpdatingDateInterval}
              containerClassName={`${showReset ? 'mb-1' : 'mb-5'} flex flex-col`}
            />
            {!isUpdatingForm && !!showReset && onResetDates && (
              <Link
                href="#"
                text="Reset dates"
                className="!text-2 w-fit"
                variant="link-with-icon"
                icon="redo"
                iconPosition="left"
                onClick={onResetDates}
                as="button"
              />
            )}
            <div className="text-2">
              {rangeExplanationText}
              <Link
                text="Find your compliance period."
                variant="text-link-2"
                className="ml-1 inline-flex"
                href="https://www.pli.edu/compliance_period" //TODO: have to change once compliance period page is available on Epi
                target="_blank"
              />
            </div>
          </div>
        </Root>
      );
    }
  )
);

ComplianceForm.displayName = 'ComplianceForm';
export default ComplianceForm;
